﻿import { RESTError } from "../../API/RESTError";
import { IUnitsConversionSet } from "../../Converters/UnitsConverter/IUnitsConverter";
import { DefaultParameterDefinitions } from "../../DefaultParameterDefinitions";
import { InfluxDataWrapper } from "../../InfluxDb/InfluxDataWrapper";
import { getHistoricData } from "../../InfluxDb/InfluxDb";
import { MainSite } from "../../MainSite";
import { ParameterDefinition } from "../../Model/ParameterDefinition";
import { PlottingPage } from "./PlottingPage";

export class ParameterNodeDownloadHandler {
    constructor(public mainSite: MainSite, public plottingPage: PlottingPage, public store: string, public stationId: string, public sensorId: string, public parameterId: string, public variantId: string) {
    
    this.showData = (renderTitle: (icon: string) => void) => {
        if (this.activeJqXHR) {
            this.activeJqXHR.abort();
            this.activeJqXHR = null;
        }

        let mm = this.plottingPage.getCurrentMoments();
        var from = mm.from;
        var to = mm.to;

        var jqXHR = this.activeJqXHR = getHistoricData(this.store, this.stationId, this.sensorId, this.parameterId, this.variantId, from.valueOf(), to.valueOf(),
            (err: RESTError, data: InfluxDataWrapper) => {
                if (jqXHR == this.activeJqXHR) {
                    // This callback may occur indicating an abort after a new AJAX request has
                    // started
                    this.activeJqXHR = null;
                    this.setLoadingIcon(false, renderTitle);
                }

                if (err) {
                    if (!err.Aborted) {
                        alert('Error getting data: ' + err.Message);
                    }
                    return;
                }

                if (!data.VariantId && this.variantId != null) {
                    data.VariantId = this.variantId;
                }

                if (!data.ParameterDefinition) {
                    var parameterDefinition: ParameterDefinition = this.plottingPage.mainSite.staticDataManager.Schema.ParameterSchema.Stations[this.stationId].Sensors[this.sensorId].Parameters[this.parameterId].Variants.filter(v => v.Variant == this.variantId)[0].ParameterDefinition;

                    if (!parameterDefinition || parameterDefinition.Default) {
                        parameterDefinition = DefaultParameterDefinitions.Parameters[this.parameterId];
                    }

                    if (parameterDefinition) {
                        data.ParameterDefinition = parameterDefinition;
                    }
                }

                let unitsConversionSet: IUnitsConversionSet | null = MainSite.getUnitConverter().getConversionSet(data.ParameterDefinition?.Units ?? '', this.parameterId);
                // console.info('ParameterNodeDownloadHandler.showData - unitsConversionSet', unitsConversionSet, data);
                if (unitsConversionSet) {
                    data.Data = data.Data.map(x => {
                        if (Array.isArray(x) && x.length == 2) {
                            return [x[0], unitsConversionSet.convertValue(x[1])];
                        }

                        return x;
                    });
                }

                this.currentData = data;
                this.currentResolution = data.Resolution;
                this.plottingPage.addOrUpdateChartSeries(data);
            }, this.plottingPage.desiredResolution);

        this.setLoadingIcon(true, renderTitle);
    }

    /**
     * Refresh the data for this node (added while implementing auto-refresh feature of plot page).
     * @param tsFrom UTC timestamp from
     * @param tsTo UTC timestamp until
     * @param resolution The resolution of current data for sake of consistancy
     */
    this.refreshData = (tsFrom: number, tsTo: number, resolution: number = null, renderTitle: (icon: string) => void) => {
        if (this.activeJqXHR) {
            console.info(`[ParameterNodeDownloadHandler] Not performing refresh on server as a current AJAX operation is still ongoing.`);
            return; //: just cancel this operation, to let the current ongoing AJAX call complete.
            this.activeJqXHR.abort();
            this.activeJqXHR = null;
        }

        if (resolution == null) {
            resolution = this.currentResolution;
        }

        var jqXHR = this.activeJqXHR = getHistoricData(this.store, this.stationId, this.sensorId, this.parameterId, this.variantId, tsFrom, tsTo,
            (err: RESTError, data: InfluxDataWrapper) => {
                if (jqXHR == this.activeJqXHR) {
                    // This callback may occur indicating an abort after a new AJAX request has
                    // started
                    this.activeJqXHR = null;
                    this.setLoadingIcon(false, renderTitle);
                }

                if (err) {
                    if (!err.Aborted) {
                        // alert('Error getting data: ' + err.Message);
                        console.warn(`[ParameterNodeDownloadHandler] Error getting data: ${err.Message}`);
                    }
                    return;
                }

                if (!data.VariantId && this.variantId != null) {
                    data.VariantId = this.variantId;
                }

                if (!data.ParameterDefinition) {
                    var parameterDefinition: ParameterDefinition = this.plottingPage.mainSite.staticDataManager.Schema.ParameterSchema.Stations[this.stationId].Sensors[this.sensorId].Parameters[this.parameterId].Variants.filter(v => v.Variant == this.variantId)[0].ParameterDefinition;

                    if (!parameterDefinition || parameterDefinition.Default) {
                        parameterDefinition = DefaultParameterDefinitions.Parameters[this.parameterId];
                    }

                    if (parameterDefinition) {
                        data.ParameterDefinition = parameterDefinition;
                    }
                }

                let unitsConversionSet: IUnitsConversionSet | null = MainSite.getUnitConverter().getConversionSet(data.ParameterDefinition?.Units ?? '', this.parameterId);
                // console.info('ParameterNodeDownloadHandler.refreshData - unitsConversionSet', unitsConversionSet, data);
                if (unitsConversionSet) {
                    data.Data = data.Data.map(x => {
                        if (Array.isArray(x) && x.length == 2) {
                            return [x[0], unitsConversionSet.convertValue(x[1])];
                        }

                        return x;
                    });
                }

                //: merge data rather than replace:
                let old: InfluxDataWrapper = this.currentData;
                ///this.currentData.Data = [].concat([], this.currentData.Data, data.Data);
                this.currentData.Data = this.plottingPage.dataPointsConcat(this.currentData.Data, data.Data);
                this.currentData.TimestampFirst = Math.min(old.TimestampFirst, data.TimestampFirst);
                this.currentData.TimestampLast = Math.max(old.TimestampLast, data.TimestampLast);
                // console.info(`[ParameterNodeDownloadHandler] New data: `, { old: old, currentData: this.currentData, data: data });
                this.plottingPage.addOrUpdateChartSeries(data, true);
            }, resolution);

        this.setLoadingIcon(true, renderTitle);
    }

    this.hideData = (renderTitle: (icon: string) => void) => {
        if (this.activeJqXHR) {
            this.activeJqXHR.abort();
            this.activeJqXHR = null;

            this.setLoadingIcon(false, renderTitle);
        }

        if (this.currentData) {
            this.plottingPage.removeChartSeries(this.currentData);
            this.currentData = null;
        }
    }

    this.setLoadingIcon = (loading: boolean, renderTitle: (icon: string) => void) => {
        renderTitle(loading ? '/images/icons/treeLoadingSpinner.gif' : '/images/icons/lineChartIcon.png');
    }
    }

    private activeJqXHR: JQueryXHR = null;
    private currentData: InfluxDataWrapper = null;
    private currentResolution: number = -1;

    /**
     * Fancytree ends up looking at this icon property (via node.data.icon) when
     * node.renderTitle is called.
     */
    public icon: string = null;

    public showData: (renderTitle: (icon: string) => void) => void;
    public hideData: (renderTitle: (icon: string) => void) => void;
    public refreshData: (tsFrom: number, tsTo: number, resolution: number, renderTitle: (icon: string) => void) => void;
    private setLoadingIcon: (loading: boolean, renderTitle: (icon: string) => void) => void;
}

 